import { useCallback, useRef, useState } from "react";

const T = 5;

const best = (cands, targets) => {
  let hit = null;
  cands.forEach(([off, v]) => targets.forEach((t) => {
    const d = t - v;
    if (Math.abs(d) <= T && (!hit || Math.abs(d) < Math.abs(hit.d))) hit = { d, pos: t, off };
  }));
  return hit;
};

// Snaps a moving rect to other blocks' edges/centres, then to the grid; returns guides to draw.
export function snapMove(r, others, grid, pageW) {
  const xs = [68, pageW / 2, pageW - 56, ...others.flatMap((o) => [o.x, o.x + o.w / 2, o.x + o.w])];
  const ys = others.flatMap((o) => [o.y, o.y + o.h / 2, o.y + o.h]);
  const hx = best([[0, r.x], [r.w / 2, r.x + r.w / 2], [r.w, r.x + r.w]], xs);
  const hy = best([[0, r.y], [r.h / 2, r.y + r.h / 2], [r.h, r.y + r.h]], ys);
  const g = (v) => (grid ? Math.round(v / grid) * grid : Math.round(v));
  return {
    x: hx ? Math.round(r.x + hx.d) : g(r.x),
    y: hy ? Math.round(r.y + hy.d) : g(r.y),
    guides: [...(hx ? [{ v: true, pos: hx.pos }] : []), ...(hy ? [{ v: false, pos: hy.pos }] : [])],
  };
}

export function snapWidth(r, w, others, grid, pageW) {
  const right = r.x + w;
  const hit = best([[0, right]], [pageW / 2, pageW - 56, ...others.flatMap((o) => [o.x, o.x + o.w])]);
  if (hit) return { w: Math.max(10, Math.round(w + hit.d)), guides: [{ v: true, pos: hit.pos }] };
  return { w: Math.max(10, grid ? Math.round(right / grid) * grid - r.x : Math.round(w)), guides: [] };
}

// Undo/redo history; changes sharing a group key within 800ms merge into one step.
export function useHistory(initial) {
  const [state, setState] = useState({ past: [], present: initial, future: [] });
  const last = useRef({ key: null, t: 0 });
  const set = useCallback((value, key) => {
    const now = Date.now();
    const merge = key && last.current.key === key && now - last.current.t < 800;
    last.current = { key, t: now };
    setState((s) => ({ past: merge ? s.past : [...s.past, s.present].slice(-100), present: value, future: [] }));
  }, []);
  const undo = useCallback(() => { last.current.key = null; setState((s) => (s.past.length ? { past: s.past.slice(0, -1), present: s.past[s.past.length - 1], future: [s.present, ...s.future] } : s)); }, []);
  const redo = useCallback(() => { last.current.key = null; setState((s) => (s.future.length ? { past: [...s.past, s.present], present: s.future[0], future: s.future.slice(1) } : s)); }, []);
  const reset = useCallback((value) => { last.current.key = null; setState({ past: [], present: value, future: [] }); }, []);
  return { value: state.present, set, undo, redo, reset, canUndo: state.past.length > 0, canRedo: state.future.length > 0 };
}
